import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { CallStatus, useCallable } from '../../lib/useCallable';

/**
 * The firm's people index: how many it holds, when it was last built, and a button to rebuild it.
 *
 * The index is what the "Copy others" box on the conflict check request searches. It is rebuilt
 * every night and can be rebuilt here, so a new starter set up this morning is findable this
 * morning rather than tomorrow.
 *
 * The kept count is shown NEXT TO the number of accounts examined because the gap between them is
 * the rule doing its work: every enabled account on the domain is examined, and only those with
 * at least two of department, office and job title are kept. If the count ever looks wrong in
 * either direction, that ratio is the first thing to look at.
 */

/** A time that may arrive as an ISO string, milliseconds, or a serialised Firestore Timestamp. */
function whenText(value) {
  if (!value) return 'Never';
  const seconds = value.seconds ?? value._seconds;
  const date =
    typeof seconds === 'number' ? new Date(seconds * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
}

export default function DirectoryPeople() {
  const { run, busy, error, saved } = useCallable();
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [lastRun, setLastRun] = useState(null);

  const refresh = async () => {
    try {
      // The search reports the index's size and age with every answer. Two characters is the
      // shortest query it accepts, and this one is chosen to match almost nobody.
      const result = await httpsCallable(functions, 'searchDirectory')({ query: 'zq' });
      setStatus(result.data || null);
      setStatusError('');
    } catch (err) {
      setStatusError(err.message || String(err));
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const rebuild = async () => {
    // Paging a whole tenant out of Graph can outlast the SDK's 70-second default; the function
    // itself is allowed five minutes, and the browser has to wait at least as long.
    const result = await run('adminSyncDirectoryIndex', {}, { timeout: 300000 });
    if (result) {
      setLastRun(result);
      await refresh();
    }
  };

  return (
    <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">People at the firm</h3>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
        Everyone at the firm with an enabled account, read from Entra. This is what the
        “Copy others” box on the conflict check request searches. It is rebuilt every night at
        6:30, or now.
      </p>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        An account counts as a person when at least two of department, office and job title are
        filled in. That rule is what keeps scanners, meeting rooms and service accounts out. The
        list is never sent to a browser; the box only ever sees matches for what was typed.
      </p>

      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-slate-500 dark:text-slate-400">People in the index</dt>
          <dd className="font-medium text-ink dark:text-slate-100">
            {status ? status.indexed : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500 dark:text-slate-400">Last rebuilt</dt>
          <dd className="font-medium text-ink dark:text-slate-100">
            {status ? whenText(status.syncedAt) : '—'}
          </dd>
        </div>
        {lastRun && (
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400">This rebuild</dt>
            <dd className="font-medium text-ink dark:text-slate-100">
              {lastRun.examined} accounts examined, {lastRun.count} kept
            </dd>
          </div>
        )}
      </dl>
      {statusError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{statusError}</p>}

      <button
        type="button"
        onClick={rebuild}
        disabled={busy}
        className="mt-4 rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
      >
        {busy ? 'Rebuilding…' : 'Rebuild from Entra now'}
      </button>
    </section>
  );
}
